import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase/config";
import {
  doc, getDoc, updateDoc, setDoc, deleteDoc,
  arrayUnion, arrayRemove,
} from "firebase/firestore";

export const useJournalShare = (childId) => {
  const [viewerEmails,         setViewerEmails]  = useState([]);
  const [publicJournalEnabled, setPublicEnabled] = useState(false);
  const [publicJournalId,      setPublicId]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!childId) { setLoading(false); return; }
    getDoc(doc(db, "children", childId))
      .then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setViewerEmails(d.viewerEmails || []);
          setPublicEnabled(d.publicJournalEnabled || false);
          setPublicId(d.publicJournalId || null);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [childId]);

  const addViewer = useCallback(async (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || viewerEmails.includes(trimmed)) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "children", childId), {
        viewerEmails: arrayUnion(trimmed),
      });
      setViewerEmails((prev) => [...prev, trimmed]);
    } finally { setSaving(false); }
  }, [childId, viewerEmails]);

  const removeViewer = useCallback(async (email) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "children", childId), {
        viewerEmails: arrayRemove(email),
      });
      setViewerEmails((prev) => prev.filter((e) => e !== email));
    } finally { setSaving(false); }
  }, [childId]);

  const enablePublicLink = useCallback(async (childName) => {
    const id = crypto.randomUUID();
    setSaving(true);
    try {
      await updateDoc(doc(db, "children", childId), {
        publicJournalEnabled: true,
        publicJournalId: id,
      });
      await setDoc(doc(db, "publicJournalLinks", id), {
        childId,
        childName: childName || "",
        createdAt: new Date().toISOString(),
      });
      setPublicEnabled(true);
      setPublicId(id);
    } finally { setSaving(false); }
  }, [childId]);

  const disablePublicLink = useCallback(async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "children", childId), {
        publicJournalEnabled: false,
      });
      setPublicEnabled(false);
    } finally { setSaving(false); }
  }, [childId]);

  const regeneratePublicLink = useCallback(async (childName) => {
    const newId = crypto.randomUUID();
    setSaving(true);
    try {
      if (publicJournalId) {
        await deleteDoc(doc(db, "publicJournalLinks", publicJournalId));
      }
      await updateDoc(doc(db, "children", childId), {
        publicJournalEnabled: true,
        publicJournalId: newId,
      });
      await setDoc(doc(db, "publicJournalLinks", newId), {
        childId,
        childName: childName || "",
        createdAt: new Date().toISOString(),
      });
      setPublicEnabled(true);
      setPublicId(newId);
    } finally { setSaving(false); }
  }, [childId, publicJournalId]);

  return {
    viewerEmails, publicJournalEnabled, publicJournalId,
    loading, saving,
    addViewer, removeViewer,
    enablePublicLink, disablePublicLink, regeneratePublicLink,
  };
};
